import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, Clock, CheckCircle, XCircle, ArrowLeft, ExternalLink, RefreshCw, Store, Receipt, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { translate as defaultTranslate } from "@/i18n";
import {
    vendorDisplayLabel,
    vendorPageHref,
    vendorProfileHref,
} from "../vendorLink";
import { transactionDisplayAmount, transactionDisplayCurrency } from "../transactionDisplay";

// Transaction types live in client-ecommerce.ts; this component uses a minimal view shape.

export interface Transaction {
    id: string;
    created_at: string;
    status: string;
    total_amount: number;
    currency: string;
    product_info: any[];
    shipping_info: any;
    payment_provider?: string;
    external_order_id?: string;
    metadata?: Record<string, any>;
}

export interface PurchasesListProps {
    /** Async function to fetch user transactions. */
    onFetchTransactions: () => Promise<Transaction[]>;
    /** Navigation callback. */
    onNavigate: (path: string) => void;
    /** Toast notification handler. */
    toast?: {
        error: (msg: string) => void;
    };
    /** Hide standalone page chrome when embedded in profile. */
    embedded?: boolean;
    /** Highlight a specific order (e.g. after checkout success). */
    highlightOrderId?: string;
    /** Translation function — English key in, localized string out. */
    t?: (key: string) => string;
}

function buildStatusConfig(t: (key: string) => string): Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> {
    return {
        pending: { label: t("Pending"), variant: "secondary", icon: <Clock className="h-3.5 w-3.5" /> },
        processing: { label: t("Processing"), variant: "default", icon: <RefreshCw className="h-3.5 w-3.5" /> },
        paid: { label: t("Paid"), variant: "default", icon: <CheckCircle className="h-3.5 w-3.5" /> },
        completed: { label: t("Completed"), variant: "default", icon: <CheckCircle className="h-3.5 w-3.5" /> },
        failed: { label: t("Failed"), variant: "destructive", icon: <XCircle className="h-3.5 w-3.5" /> },
        refunded: { label: t("Refunded"), variant: "outline", icon: <RefreshCw className="h-3.5 w-3.5" /> },
        cancelled: { label: t("Cancelled"), variant: "destructive", icon: <XCircle className="h-3.5 w-3.5" /> },
    };
}

const ACTIVE_STATUSES = new Set(["pending", "processing"]);

export function PurchasesList({
    onFetchTransactions,
    onNavigate,
    toast,
    embedded = false,
    highlightOrderId: highlightOrderIdProp,
    t: tProp,
}: PurchasesListProps) {
    const t = tProp ?? defaultTranslate;
    const statusConfig = buildStatusConfig(t);
    const [searchParams] = useSearchParams();
    const highlightOrderId = highlightOrderIdProp ?? searchParams.get("order") ?? undefined;

    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const highlightRef = useRef<HTMLDivElement | null>(null);

    const loadTransactions = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const data = await onFetchTransactions();
            setTransactions(data);
            return data;
        } catch (err) {
            console.error("Failed to load transactions:", err);
            toast?.error(t("Failed to load purchases"));
            return [];
        } finally {
            if (!silent) setLoading(false);
            setRefreshing(false);
        }
    }, [onFetchTransactions, toast]);

    useEffect(() => {
        void loadTransactions();
    }, [loadTransactions]);

    // Poll while any order is still pending (webhook may be in flight)
    useEffect(() => {
        const hasActive = transactions.some((tx) => ACTIVE_STATUSES.has(tx.status));
        if (!hasActive) return;

        const interval = setInterval(() => {
            void loadTransactions(true);
        }, 4000);

        const timeout = setTimeout(() => clearInterval(interval), 60_000);
        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [transactions, loadTransactions]);

    useEffect(() => {
        if (!highlightOrderId || loading) return;
        highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [highlightOrderId, loading, transactions.length]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className={embedded ? "space-y-4" : "mx-auto max-w-4xl px-4 py-8 space-y-6"}>
            {!embedded && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Package className="h-6 w-6" />
                        <h1 className="text-2xl font-bold">{t("My Purchases")}</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void loadTransactions(true)}
                            disabled={refreshing}
                        >
                            <RefreshCw className={cn("h-4 w-4 mr-1", refreshing && "animate-spin")} />
                            {t("Refresh")}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onNavigate("/")}>
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            {t("Back")}
                        </Button>
                    </div>
                </div>
            )}

            {embedded && (
                <div className="flex justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void loadTransactions(true)}
                        disabled={refreshing}
                    >
                        <RefreshCw className={cn("h-4 w-4 mr-1", refreshing && "animate-spin")} />
                        {t("Refresh")}
                    </Button>
                </div>
            )}

            {transactions.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
                        <Package className="h-12 w-12 text-muted-foreground" />
                        <p className="text-muted-foreground">{t("No purchases yet.")}</p>
                        <Button variant="outline" onClick={() => onNavigate("/")}>
                            {t("Browse Products")}
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {transactions.map((tx) => {
                        const products = Array.isArray(tx.product_info) ? tx.product_info : [];
                        const sc = statusConfig[tx.status] || statusConfig.pending;
                        const displayCurrency = transactionDisplayCurrency(tx);
                        const displayAmount = transactionDisplayAmount(tx);
                        const date = new Date(tx.created_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        });

                        return (
                            <div
                                key={tx.id}
                                ref={highlightOrderId === tx.id ? highlightRef : undefined}
                            >
                            <Card
                                className={cn(
                                    "overflow-hidden transition-shadow",
                                    highlightOrderId === tx.id && "ring-2 ring-primary shadow-md",
                                )}
                            >
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="space-y-1">
                                            <CardTitle className="text-base">
                                                {t("Order")} #{tx.id.slice(0, 8)}
                                            </CardTitle>
                                            <p className="text-xs text-muted-foreground">{date}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge variant={sc.variant} className="flex items-center gap-1">
                                                {sc.icon}
                                                {sc.label}
                                            </Badge>
                                            <span className="font-semibold text-sm">
                                                {displayCurrency} {displayAmount.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    {/* Product list */}
                                    <div className="space-y-2">
                                        {products.map((item: any, idx: number) => {
                                            const sellerLabel = vendorDisplayLabel(item);
                                            const sellerHref = vendorProfileHref(item);
                                            const productHref = vendorPageHref(item);

                                            return (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                                            >
                                                {item.image && (
                                                    <img
                                                        src={item.image}
                                                        alt={item.title}
                                                        className="h-10 w-10 rounded object-cover shrink-0"
                                                    />
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium truncate">
                                                        {item.title || t("Product")}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {t("Qty")}: {item.quantity || 1}
                                                        {item.variant && ` · ${item.variant}`}
                                                        {" · "}
                                                        {displayCurrency} {Number(item.price || 0).toFixed(2)}
                                                    </p>
                                                    {sellerHref && (
                                                        <a
                                                            href={sellerHref}
                                                            onClick={(e) => { e.preventDefault(); onNavigate(sellerHref); }}
                                                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5 cursor-pointer"
                                                        >
                                                            <Store className="h-3 w-3" />
                                                            {sellerLabel ? `${t("Sold by")} ${sellerLabel}` : t("View seller")}
                                                        </a>
                                                    )}
                                                </div>
                                                {productHref && (
                                                    <a
                                                        href={productHref}
                                                        onClick={(e) => { e.preventDefault(); onNavigate(productHref); }}
                                                        className="text-primary hover:underline text-xs flex items-center gap-1 shrink-0 cursor-pointer"
                                                    >
                                                        {t("View")}
                                                        <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>

                                    {/* Shipping summary */}
                                    {tx.shipping_info && Object.keys(tx.shipping_info).length > 0 && (
                                        <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                                            {t("Ships to")}: {(tx.shipping_info as any).fullName}, {(tx.shipping_info as any).city}, {(tx.shipping_info as any).country}
                                        </div>
                                    )}

                                    {/* Receipt / Invoice links */}
                                    {tx.metadata && (tx.metadata.stripe_receipt_url || tx.metadata.stripe_invoice_url) && (
                                        <div className="mt-3 pt-3 border-t flex items-center gap-4">
                                            {tx.metadata.stripe_receipt_url && (
                                                <a
                                                    href={tx.metadata.stripe_receipt_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                                >
                                                    <Receipt className="h-3.5 w-3.5" />
                                                    {t("View Receipt")}
                                                </a>
                                            )}
                                            {tx.metadata.stripe_invoice_url && (
                                                <a
                                                    href={tx.metadata.stripe_invoice_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                                >
                                                    <FileText className="h-3.5 w-3.5" />
                                                    {t("View Invoice")}
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
